import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  CircularProgress,
} from "@mui/material";
import {
  TrendingUp,
  CalendarMonth,
  Today,
  ShowChart,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { AppContext } from "../../context/AppContext";
import SidebarMenu from "./AdminPanel/SidebarMenu";
import TopBar from "./AdminPanel/TopBar";
import SalesChartPanel from "./Statistics/SalesChartPanel";

const money = (value) => {
  const n = Number(value || 0);
  return `$${n.toFixed(2)}`;
};

const sumItems = (items) => {
  return items.reduce((acc, item) => acc + Number(item.neto || 0), 0);
};

const getBestItem = (items) => {
  if (!items.length) return null;

  return items.reduce((best, item) => {
    return Number(item.neto || 0) > Number(best.neto || 0) ? item : best;
  }, items[0]);
};

const parseDate = (raw) => {
  if (!raw) return null;

  const value = String(raw);

  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return new Date(`${value}T00:00:00`);
  }

  return new Date(value);
};

const formatBestLabel = (item) => {
  if (!item) return "Sin datos";

  const raw = item.fecha || item.mes;
  const date = parseDate(raw);

  if (!date || Number.isNaN(date.getTime())) {
    return String(raw || "Sin datos");
  }

  if (item.mes) {
    return date.toLocaleDateString("es-EC", {
      month: "long",
      year: "numeric",
    });
  }

  return date.toLocaleDateString("es-EC", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
};

const StatCard = ({ title, value, subtitle, icon: Icon, loading }) => {
  return (
    <Paper
      sx={{
        p: 2.4,
        borderRadius: 3,
        bgcolor: "#fff",
        border: "1px solid #e5eaf2",
        boxShadow: "0 8px 26px rgba(15, 23, 42, 0.06)",
        height: "100%",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: 13,
              fontWeight: 800,
              color: "#64748b",
              mb: 0.8,
            }}
          >
            {title}
          </Typography>

          {loading ? (
            <CircularProgress size={22} />
          ) : (
            <Typography
              sx={{
                fontSize: { xs: 22, sm: 25 },
                fontWeight: 900,
                color: "#152238",
                lineHeight: 1.1,
                wordBreak: "break-word",
              }}
            >
              {value}
            </Typography>
          )}

          <Typography
            sx={{
              fontSize: 12.5,
              fontWeight: 600,
              color: "#64748b",
              mt: 0.8,
              lineHeight: 1.35,
            }}
          >
            {subtitle}
          </Typography>
        </Box>

        <Box
          sx={{
            width: 43,
            height: 43,
            borderRadius: "50%",
            bgcolor: "#0870A2",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <Icon sx={{ color: "#fff", fontSize: 23 }} />
        </Box>
      </Box>
    </Paper>
  );
};

const AdminStatisticsContent = () => {
  const { accessToken } = useContext(AppContext);

  const [loading, setLoading] = useState(false);
  const [weekItems, setWeekItems] = useState([]);
  const [monthItems, setMonthItems] = useState([]);
  const [yearItems, setYearItems] = useState([]);

  useEffect(() => {
    const loadData = async () => {
      if (!accessToken) return;

      setLoading(true);

      try {
        const now = new Date();
        const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;

        const [weekRes, monthRes, yearRes] = await Promise.all([
          fetch("/dashboard/ventas/?periodo=semana", {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          }),
          fetch(`/dashboard/ventas/?periodo=mes&mes=${currentMonth}`, {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          }),
          fetch("/dashboard/ventas/?periodo=anio", {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          }),
        ]);

        const [weekData, monthData, yearData] = await Promise.all([
          weekRes.json(),
          monthRes.json(),
          yearRes.json(),
        ]);

        setWeekItems(weekRes.ok && Array.isArray(weekData) ? weekData : []);
        setMonthItems(monthRes.ok && Array.isArray(monthData) ? monthData : []);
        setYearItems(yearRes.ok && Array.isArray(yearData) ? yearData : []);
      } catch {
        setWeekItems([]);
        setMonthItems([]);
        setYearItems([]);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [accessToken]);

  const summary = useMemo(() => {
    const weekTotal = sumItems(weekItems);
    const monthTotal = sumItems(monthItems);
    const yearTotal = sumItems(yearItems);
    const bestMonth = getBestItem(yearItems);
    const bestDay = getBestItem(monthItems);

    return {
      weekTotal,
      monthTotal,
      yearTotal,
      bestMonth,
      bestDay,
    };
  }, [weekItems, monthItems, yearItems]);

  return (
    <Box
      sx={{
        width: "95%",
        mx: "auto",
        mt: { xs: 2, sm: 3 },
        mb: 5,
      }}
    >
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} lg={3}>
          <StatCard
            title="Ventas de la semana"
            value={money(summary.weekTotal)}
            subtitle="Últimos 8 días registrados"
            icon={Today}
            loading={loading}
          />
        </Grid>

        <Grid item xs={12} sm={6} lg={3}>
          <StatCard
            title="Ventas del mes"
            value={money(summary.monthTotal)}
            subtitle="Mes actual completo"
            icon={CalendarMonth}
            loading={loading}
          />
        </Grid>

        <Grid item xs={12} sm={6} lg={3}>
          <StatCard
            title="Ventas del año"
            value={money(summary.yearTotal)}
            subtitle="Acumulado anual neto"
            icon={TrendingUp}
            loading={loading}
          />
        </Grid>

        <Grid item xs={12} sm={6} lg={3}>
          <StatCard
            title="Mejor período"
            value={money(Number(summary.bestMonth?.neto || summary.bestDay?.neto || 0))}
            subtitle={
              summary.bestMonth
                ? `Mes destacado: ${formatBestLabel(summary.bestMonth)}`
                : summary.bestDay
                ? `Día destacado: ${formatBestLabel(summary.bestDay)}`
                : "Sin datos todavía"
            }
            icon={ShowChart}
            loading={loading}
          />
        </Grid>

        <Grid item xs={12}>
          <SalesChartPanel accessToken={accessToken} />
        </Grid>
      </Grid>
    </Box>
  );
};

const AdminStatistics = () => {
  const [open, setOpen] = useState(false);
  const [submenuOpen, setSubmenuOpen] = useState(false);

  const { user } = useContext(AppContext);
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = "D&R Soluciones Informáticas | Estadísticas";

    if (!user.isLoggedIn) {
      navigate("/login", { replace: true });
    }
  }, [user.isLoggedIn, navigate]);

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", backgroundColor: "#f1f5ff" }}>
      <SidebarMenu
        open={open}
        setOpen={setOpen}
        submenuOpen={submenuOpen}
        setSubmenuOpen={setSubmenuOpen}
        navigate={navigate}
      />

      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <TopBar open={open} setOpen={setOpen} />
        <AdminStatisticsContent />
      </Box>
    </Box>
  );
};

export default AdminStatistics;